<template>
  <div class="container">
    <div class="row">
      <div class="col-12 page-header">
        <h1>Prediction Accuracy</h1>
        <p>See how we stack up against the competition.</p>
      </div>
    </div>
    <div class="row">
      <div class="col-12 grid-wrapper accuracy-panel">
        <div class="grid-item accuracy-form">
          <div class="row form-wrapper">
            <div class="col-lg-8 offset-lg-2 col-12">
              <div class="form-group select-wrapper">
                <label><span>Select a Year</span></label>
                <select class="form-control" v-model="year">
                  <template v-for="(year, idx) in years">
                    <option v-bind:key="'year-options-' + year + idx" :value="year">
                      {{ year }}
                    </option>
                  </template>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-item">
          <v-inline-svg :src="require('@/assets/images/icon-predicted.svg')" />
          <h2>Prediction Accuracy: {{ year }}</h2>
          <v-loading-spinner v-if="loading"/>
          <v-prediction-accuracy-scatter-chart :data="data" v-if="!loading && data.length > 0" />
        </div>
        <div class="grid-item">
          <h2>DraftPrime's {{ year }} R<sup>2</sup> Value</h2>
          <h3>{{ regression.rsquared | roundToThousandth }}</h3>
          <div class="row">
            <p class="heading col-12">Other Competitors*</p>
            <v-loading-spinner v-if="loading"/>
            <template v-if="Object.keys(competitors).length > 0">
              <div
                v-for="(competitor, idx) in competitors"
                class="competitor-row"
                :key="competitor.name"
              >
                <div class="col-8" v-bind:key="'competitor-value-name-' + idx">
                  <p>{{ competitor.name }}</p>
                </div>
                <div class="col-4" v-bind:key="'competitor-value-rvalue-' + idx">
                  <p :class="{ 'highest' : competitor.rsquared >= competitorMaxRSquared}">
                    {{ competitor.rsquared }}
                  </p>
                </div>
              </div>
            </template>
            <template v-if="Object.keys(competitors).length == 0 && !loading">
              <p class="col-12 text-center mt-3 mb-4" style="color: lightgray;">
                (No competitor data for {{ year }})
              </p>
            </template>
          </div>
          <div class="row small-print">
            <h4>A higher number indicates better prediction accuracy</h4>
            <h4 class="mt-2"><a href="https://fantasyfootballanalytics.net/2017/03/best-fantasy-football-projections-2017.html" target="_blank">* Data source</a></h4>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PredictionDataApi from '~/api/PredictionData';

export default {
  name: 'PredictionAccuracy',

  computed: {
    years() {
      return _.range((new Date()).getFullYear() - 1, 2010);
    },
    competitors() {
      const data = Object.keys(this.competitorsData)
        .map((k) => {
          return {
            name: k,
            rsquared: this.$options.filters.roundToThousandth(this.competitorsData[k]),
          };
        });
      return _.chain(data).sortBy((o) => o.rsquared).reverse().value();
    },
    competitorMaxRSquared() {
      return _.max(this.competitors.map((c) => c.rsquared)) || 0;
    },
  },

  data() {
    return {
      loading: false,
      year: (new Date()).getFullYear() - 1,
      regression: {},
      data: [],
      competitorsData: {},
    };
  },

  watch: {
    year() {
      this.updateGraphData();
    },
  },

  methods: {
    async updateGraphData() {
      this.loading = true;
      this.data = [];
      this.competitorsData = {};
      this.regression = {};
      const results = await PredictionDataApi.get(this.year);
      this.data = _.get(results, 'data.data', []);
      this.competitorsData = _.get(results, 'data.competitors', {});
      this.regression = _.get(results, 'data.regression', {});
      this.loading = false;
    },
  },

  mounted() {
    this.updateGraphData();
  },

  metaInfo: {
    meta: [
      { name: 'description', content: 'See how we stack up against the competition.' },
    ],
  },
};
</script>

<style lang="scss">
  @import '@/assets/scss/_component_helpers.scss';

  .page-prediction-accuracy {

    h3 {
      color: $color-primary-darker;
      text-align: center;
    }

    p.heading {
      color: $color-primary-darker-2;
      font-weight: 600;
      text-align: center;
      margin-top: 20px;
    }

    .grid-wrapper.accuracy-panel {
      display: grid;
      grid-row-gap: 25px;
      grid-column-gap: 25px;
      grid-template-columns: 75% auto;
      width: 100%;

      @include media-breakpoint-only(xl) {
        grid-template-columns: 65% auto;
      }

      @include media-breakpoint-down(md) {
        grid-template-columns: 100%;
      }

      .grid-item {
        padding: 40px;
        border-radius: $panel-rounded-corner;
        box-shadow: $box-shadow;
      }

      .grid-item:nth-of-type(1) {
        grid-column: 1;
        grid-row: 1;
      }

      .grid-item:nth-of-type(2) {
        grid-column: 1;
        grid-row: 2;
        position: relative;

        @include media-breakpoint-down(md) {
          grid-column: 1;
          grid-row: 2;
        }

        img {
          width: 100%;
        }

        h2 {
          margin-left: 60px;
          text-align: left;
          color: $color-primary-darker-3;
          margin-bottom: 30px;
        }

        .svg-wrapper {
          position: absolute;
          top: 36px;
          max-width: 40px;

          svg {
            width: 100%;

            path {
              fill: $color-primary-darker-3;
            }
          }
        }
      }

      .grid-item:nth-of-type(3) {
        grid-column: 2;
        grid-row: 1/3;
        padding: 0;
        padding-top: 40px;

        @include media-breakpoint-down(md) {
          grid-column: 1;
          grid-row: 3;
        }

        .row {
          margin: 0;
          padding: 13px;
          border-top: 1px solid $color-primary-lightest;
        }

        .highest {
          color: $color-primary-darker;
          font-weight: 500;
        }

        h2,
        .small-print {
          padding-left: 40px;
          padding-right: 40px;

          a {
            color: $color-primary-darker;

            &:hover {
              color: $color-secondary-darker;
            }
          }
        }
      }

      .competitor-row {
        display: flex;
        flex: 0 0 100%;
      }
    }
  }
</style>
